import type { SalesSummary } from '@/types/SalesSummary.interface';

/**
 * SATIŞ RAPORU GÖRÜNÜM MODELİ — admin "Satış Raporu" ile web POS "Raporlar"
 * ekranının ORTAK kuralları: hızlı seçim aralıkları, istek parametreleri,
 * hangi satırın hangi sırayla gösterildiği ve sayı biçimi.
 *
 * Bu dosya `uyanik-admin/src/utils/salesReport.ts` ve
 * `uyanik-web/src/utils/salesReport.ts`'te BİREBİR aynıdır (yalnız tip import
 * yolu farklı). Birini değiştirirsen ötekini de değiştir; aksi hâlde iki ekran
 * yine farklı rakam/satır gösterir. Etiketler anahtar olarak döner: admin
 * i18next ile, web POS `src/constants/salesReportLabels.ts` ile çevirir (aynı
 * Türkçe metinler).
 */

export type ReportPreset =
  | 'today'
  | 'yesterday'
  | 'last.7days'
  | 'last.30days'
  | 'last.365days'
  | 'custom';

/** Hızlı seçim sırası (madde 10) — iki ekranda aynı. */
export const REPORT_PRESETS: ReportPreset[] = [
  'today',
  'yesterday',
  'last.7days',
  'last.30days',
  'last.365days',
  'custom'
];

/** Şube seçili değilken bile rapor İstanbul saatine göre kesilir. */
export const REPORT_TIMEZONE = 'Europe/Istanbul';
export const REPORT_TOP_PRODUCTS = 10;
export const REPORT_WASTE_PAGE_SIZE = 10;

/**
 * Günlük girişler ortalamaya katılsın mı — şube sahibinin tercihi.
 * 'separate': Ortalama Sepet = sipariş cirosu ÷ ücretli sipariş.
 * 'included': ücretli günlük girişler de eklenir (ortalama işlem).
 * Üyelikle (0 TL) girişler ve ücretsiz verilen siparişler iki modda da dışarıda.
 * Seçim cihazda `ENTRY_MODE_STORAGE_KEY` ile hatırlanır.
 */
export type EntryMode = 'separate' | 'included';
export const ENTRY_MODES: EntryMode[] = ['separate', 'included'];
export const DEFAULT_ENTRY_MODE: EntryMode = 'separate';
export const ENTRY_MODE_STORAGE_KEY = 'salesReport.entryMode';

export const isEntryMode = (value: unknown): value is EntryMode =>
  value === 'separate' || value === 'included';

export interface AverageCardModel {
  value: number;
  count: number;
  titleKey: string;
  subKey: string;
  hintKey: string;
}

/** Ortalama kartının seçili moda göre değeri ve etiket anahtarları. */
export function averageCard(
  summary: SalesSummary,
  mode: EntryMode
): AverageCardModel {
  if (mode === 'included' && summary.basketWithEntries) {
    return {
      value: summary.basketWithEntries.average,
      count: summary.basketWithEntries.count,
      titleKey: 'salesReport.averageWithEntries',
      subKey: 'salesReport.averageWithEntries.sub',
      hintKey: 'salesReport.averageWithEntries.hint'
    };
  }
  return {
    value: summary.orders.averageBasket,
    count: summary.orders.paidCount,
    titleKey: 'salesReport.averageBasket',
    subKey: 'salesReport.averageBasket.sub',
    hintKey: 'salesReport.averageBasket.hint'
  };
}

/** Hazır aralıklar: bugün DAHİL tam N işletme günü (backend `period` ile aynı). */
const PRESET_SPAN: Record<
  Exclude<ReportPreset, 'custom'>,
  { endOffset: number; days: number }
> = {
  today: { endOffset: 0, days: 1 },
  yesterday: { endOffset: 1, days: 1 },
  'last.7days': { endOffset: 0, days: 7 },
  'last.30days': { endOffset: 0, days: 30 },
  'last.365days': { endOffset: 0, days: 365 }
};

/** `YYYY-MM-DD` + gün (saat dilimi etkisiz, UTC takvim aritmetiği). */
export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days))
    .toISOString()
    .slice(0, 10);
}

/** Hazır aralığın kapsayıcı işletme günleri; `today` = içinde olunan işletme günü. */
export function presetRange(
  preset: Exclude<ReportPreset, 'custom'>,
  today: string
): { startDate: string; endDate: string } {
  const span = PRESET_SPAN[preset];
  const endDate = addDays(today, -span.endOffset);
  return { startDate: addDays(endDate, -(span.days - 1)), endDate };
}

/**
 * `GET /reports/sales-summary` sorgusu. İki ekran AYNI sırayla AYNI
 * parametreleri gönderir (DTO whitelist'li: fazladan alan 400 döner).
 * Şube verildiyse şirket gönderilmez; ikisi de yoksa çağıranın tüm kapsamı.
 */
export function salesSummaryParams(query: {
  branchId?: number | null;
  companyId?: number | null;
  startDate: string;
  endDate: string;
  timezone?: string;
  wastePage?: number;
}): Record<string, string> {
  const params: Record<string, string> = {};
  if (query.branchId) {
    params.branchId = String(query.branchId);
  } else if (query.companyId) {
    params.companyId = String(query.companyId);
  }
  params.startDate = query.startDate;
  params.endDate = query.endDate;
  params.timezone = query.timezone || REPORT_TIMEZONE;
  params.topProducts = String(REPORT_TOP_PRODUCTS);
  params.wastePage = String(query.wastePage ?? 1);
  params.wastePageSize = String(REPORT_WASTE_PAGE_SIZE);
  return params;
}

export function toQueryString(params: Record<string, string>): string {
  return Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

// ==================== BİÇİM ====================

const moneyFormat = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export const formatMoney = (value: number): string =>
  moneyFormat.format(value || 0);

export const formatCount = (value: number): string =>
  (value || 0).toLocaleString('tr-TR');

/** Pay / toplam, "%22,7" biçiminde. */
export const formatPercent = (part: number, whole: number): string =>
  `%${(whole > 0 ? (part / whole) * 100 : 0).toLocaleString('tr-TR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })}`;

export const percentOf = (part: number, whole: number): number =>
  whole > 0 ? Math.max(0, Math.min(100, (part / whole) * 100)) : 0;

/** `YYYY-MM-DD` → `18.10.2026`. */
export function formatReportDate(date: string | null): string {
  if (!date) return '—';
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

/** Trend kovası etiketi: gün `18.10`, ay `10.2026`. */
export function formatTrendLabel(date: string): string {
  const [year, month, day] = date.split('-');
  return day ? `${day}.${month}` : `${month}.${year}`;
}

/** `17` → `17:00–18:00`. */
export function formatHourRange(hour: number): string {
  const pad = (h: number) => String(h % 24).padStart(2, '0');
  return `${pad(hour)}:00–${pad(hour + 1)}:00`;
}

/** Zayi kaydı zamanı, raporun saat diliminde: `18.10.2026 11:59`. */
export function formatReportDateTime(
  iso: string,
  timezone: string = REPORT_TIMEZONE
): string {
  const parts = new Intl.DateTimeFormat('tr-TR', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`;
}

/** Başlık altı: gerçek pencere (yanıttan) — "Son 7 Gün" gerçekten 7 gün. */
export function windowParams(summary: SalesSummary): {
  start: string;
  end: string;
  days: number;
} {
  return {
    start: formatReportDate(summary.window.startDate),
    end: formatReportDate(summary.window.endDate),
    days: summary.window.days ?? 0
  };
}

// ==================== SATIRLAR ====================

export interface ReportLine {
  key: string;
  labelKey: string;
  count: number | null;
  total: number | null;
  color?: string;
  /** Açık Hesap: fiş basıldı, para alınmadı (kesikli çerçeve). */
  receivable?: boolean;
  noteKey?: string;
  noteParams?: Record<string, string | number>;
  children?: ReportLine[];
}

const hasValue = (line: { count: number | null; total: number | null }) =>
  (line.count ?? 0) > 0 || (line.total ?? 0) !== 0;

/**
 * Ödeme Türleri: kovaların toplamı Toplam Satış'a eşittir. Nakit, Kredi Kartı
 * ve Uygulama her zaman; diğer kovalar değer varsa gösterilir (hiçbiri
 * gizlice düşmez). Uygulama alt kırılımı: bakiye / online kart / puan+bakiye.
 */
export function paymentBucketRows(summary: SalesSummary): ReportLine[] {
  const b = summary.paymentBuckets;
  const line = (
    key: string,
    value: { count: number; total: number },
    color: string,
    extra: Partial<ReportLine> = {}
  ): ReportLine => ({
    key,
    labelKey: `salesReport.pay.${key}`,
    count: value.count,
    total: value.total,
    color,
    ...extra
  });

  const always = new Set(['cash', 'physicalCard', 'app']);
  const rows: ReportLine[] = [
    line('cash', b.cash, '#16A34A'),
    line('physicalCard', b.physicalCard, '#2563EB'),
    line('app', b.app, '#7C3AED', {
      children: [
        line('app.wallet', b.app.wallet, '#7C3AED'),
        line('app.onlineCard', b.app.onlineCard, '#7C3AED'),
        line('app.hybrid', b.app.hybrid, '#7C3AED')
      ].filter(hasValue)
    }),
    line('mealCard', b.mealCard, '#0D9488'),
    line('externalPlatform', b.externalPlatform, '#EA580C'),
    line('openAccount', b.openAccount, '#5D4037', { receivable: true }),
    line('loyaltyPoints', b.loyaltyPoints, '#C026D3'),
    line('campaignDirect', b.campaignDirect, '#D97706', {
      children: [
        line('campaign', b.campaignDirect.campaign, '#D97706'),
        line('direct', b.campaignDirect.direct, '#D97706')
      ].filter(hasValue)
    }),
    line('mixedUnattributed', b.mixedUnattributed, '#607D8B'),
    line('unknown', b.unknown, '#9E9E9E')
  ];
  return rows.filter((row) => always.has(row.key) || hasValue(row));
}

/**
 * Satış Detayları: ücretli sipariş / ücretli giriş / üyelikle giriş / bilet /
 * üyelik (brüt − iade = net) / paket (türe göre). Tutarların toplamı Toplam
 * Satış'tır; üyelikle giriş ciroya girmez (tutar yok).
 */
export function salesDetailRows(summary: SalesSummary): ReportLine[] {
  const { orders, dailyLogins, membershipCheckIns, tickets, memberships } =
    summary;
  const packages = summary.packages;
  return [
    {
      key: 'orders',
      labelKey: 'salesReport.detail.orders',
      count: orders.paidCount,
      total: orders.revenue,
      color: '#E91E63',
      ...(orders.pointsSpent > 0 || orders.pointsEarned > 0
        ? {
            noteKey: 'salesReport.detail.orders.points',
            noteParams: {
              spent: formatCount(orders.pointsSpent),
              earned: formatCount(orders.pointsEarned)
            }
          }
        : {})
    },
    {
      key: 'paidEntries',
      labelKey: 'salesReport.detail.paidEntries',
      count: dailyLogins.count,
      total: dailyLogins.revenue,
      color: '#3F51B5',
      noteKey: 'salesReport.detail.paidEntries.average',
      noteParams: { amount: formatMoney(dailyLogins.averagePrice) }
    },
    {
      key: 'membershipCheckIns',
      labelKey: 'salesReport.detail.membershipCheckIns',
      count: membershipCheckIns.count,
      total: null,
      color: '#9C27B0',
      noteKey: 'salesReport.detail.membershipCheckIns.hint'
    },
    {
      key: 'tickets',
      labelKey: 'salesReport.detail.tickets',
      count: tickets.count,
      total: tickets.revenue,
      color: '#00BCD4',
      ...(tickets.excluded.count > 0
        ? {
            noteKey: 'salesReport.detail.tickets.excluded',
            noteParams: {
              n: formatCount(tickets.excluded.count),
              amount: formatMoney(tickets.excluded.listValue)
            }
          }
        : {})
    },
    {
      key: 'memberships',
      labelKey: 'salesReport.detail.memberships',
      count: memberships.salesCount,
      total: memberships.netRevenue,
      color: '#009688',
      children: [
        {
          key: 'memberships.gross',
          labelKey: 'salesReport.detail.memberships.gross',
          count: memberships.salesCount,
          total: memberships.grossRevenue
        },
        {
          key: 'memberships.refunds',
          labelKey: 'salesReport.detail.memberships.refunds',
          count: memberships.refunds.count,
          // İade tutarı eksi gösterilir (−0 "-₺0,00" yazmasın diye 0 ayrı).
          total: memberships.refunds.amount
            ? -Math.abs(memberships.refunds.amount)
            : 0
        }
      ]
    },
    {
      key: 'packages',
      labelKey: 'salesReport.detail.packages',
      count: packages.count,
      total: packages.revenue,
      color: '#4CAF50',
      children: [
        {
          key: 'packages.activity',
          labelKey: 'salesReport.detail.packages.activity',
          count: packages.byType.BUNDLE_ACTIVITY.count,
          total: packages.byType.BUNDLE_ACTIVITY.total
        },
        {
          key: 'packages.product',
          labelKey: 'salesReport.detail.packages.product',
          count: packages.byType.BUNDLE_PRODUCT.count,
          total: packages.byType.BUNDLE_PRODUCT.total
        }
      ]
    }
  ];
}

const FREE_SOURCES = [
  'entryVoucher',
  'loyaltyPoints',
  'voucher',
  'entryDiscount',
  'other'
] as const;

/** Ücretsiz Verilen kaynak kırılımı (değeri olanlar). `total` = liste değeri. */
export function freeOrderRows(summary: SalesSummary): ReportLine[] {
  return FREE_SOURCES.map((key) => ({
    key,
    labelKey: `salesReport.free.${key}`,
    count: summary.freeOrders.bySource[key].count,
    total: summary.freeOrders.bySource[key].listValue
  })).filter(hasValue);
}

/** Cari Tahsilat kırılımı (Toplam Satış'a dahil DEĞİL). */
export function receivableRows(summary: SalesSummary): ReportLine[] {
  const r = summary.receivableCollections;
  return [
    {
      key: 'cash',
      labelKey: 'salesReport.pay.cash',
      count: null,
      total: r.cash
    },
    {
      key: 'physicalCard',
      labelKey: 'salesReport.pay.physicalCard',
      count: null,
      total: r.physicalCard
    },
    {
      key: 'mealCard',
      labelKey: 'salesReport.pay.mealCard',
      count: null,
      total: r.mealCard
    }
  ];
}

/** Admin'in KPI kartları (Bekleyen / Hazır / İptal) — dönemdeki durum sayıları. */
export function orderStatusTiles(summary: SalesSummary): {
  key: 'pending' | 'ready' | 'canceled';
  labelKey: string;
  value: number;
}[] {
  const s = summary.orders.statusCounts;
  return [
    {
      key: 'pending',
      labelKey: 'dashboard.pending.orders',
      value: s.preparing
    },
    { key: 'ready', labelKey: 'dashboard.ready.orders', value: s.ready },
    {
      key: 'canceled',
      labelKey: 'dashboard.canceled.orders',
      value: s.canceled
    }
  ];
}

export const WASTE_TYPE_META: Record<
  string,
  { labelKey: string; color: string }
> = {
  STAFF_MEAL: { labelKey: 'waste.type.staff.meal', color: '#2563EB' },
  TREAT: { labelKey: 'waste.type.treat', color: '#059669' },
  BREAKAGE: { labelKey: 'waste.type.breakage', color: '#D97706' },
  EXPIRED: { labelKey: 'waste.type.expired', color: '#DC2626' }
};

export const VOUCHER_REWARD_TYPES = [
  'FREE_PRODUCT',
  'PERCENT_DISCOUNT',
  'FIXED_DISCOUNT'
] as const;

/** Puan kazanım kaynakları (değeri olanlar, büyükten küçüğe). */
export function loyaltySourceRows(
  summary: SalesSummary
): { key: string; labelKey: string; points: number }[] {
  return Object.entries(summary.loyalty.earnBySource)
    .filter(([, points]) => points > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([key, points]) => ({
      key,
      labelKey: `salesReport.loyalty.source.${key}`,
      points
    }));
}
